import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { mergeClasses } from '../../utils/merge-classes';
import {
  CollapsibleBoxClasses,
  CollapsibleBoxComponent,
} from '../collapsible-box/collapsible-box.component';

export interface CollapsibleListClasses extends CollapsibleBoxClasses {
  item?: string;
  itemAlt?: string;
  itemText?: string;
  empty?: string;
  remove?: string;
}

@Component({
  selector: 'app-collapsible-list',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, CollapsibleBoxComponent],
  templateUrl: './collapsible-list.component.html',
  host: {
    class: 'block w-full',
  },
})
export class CollapsibleListComponent implements OnChanges {
  @Input({ required: true }) title!: string;
  @Input() items: string[] = [];
  @Input() emptyText = '';
  @Input() expanded = true;
  @Input() removable = false;
  @Input() striped = false;
  @Input() classes: CollapsibleListClasses = {};

  @Output() expandedChange = new EventEmitter<boolean>();
  @Output() remove = new EventEmitter<number>();

  private stripeFlags: boolean[] = [];
  private pendingRemoveIndex: number | null = null;

  get boxClasses(): CollapsibleBoxClasses {
    return {
      panel: this.classes.panel,
      header: this.classes.header,
      title: this.classes.title,
      chevron: this.classes.chevron,
      body: this.classes.body,
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['items']) return;
    const current = this.items ?? [];
    const previous = (changes['items'].previousValue ?? []) as string[];
    this.stripeFlags = nextStripeFlags(
      previous,
      current,
      this.stripeFlags,
      this.pendingRemoveIndex,
    );
    this.pendingRemoveIndex = null;
  }

  onExpandedChange(expanded: boolean): void {
    this.expanded = expanded;
    this.expandedChange.emit(this.expanded);
  }

  onRemove(index: number, event: Event): void {
    event.stopPropagation();
    this.pendingRemoveIndex = index;
    this.remove.emit(index);
  }

  itemClasses(index: number): string {
    return mergeClasses(
      'flex items-start gap-1 px-5 py-2',
      this.classes.item,
      this.striped && this.stripeFlags[index]
        ? this.classes.itemAlt || 'bg-surface-container-lowest'
        : null,
    );
  }
}

function nextStripeFlags(
  previous: string[],
  current: string[],
  flags: boolean[],
  removedIndexHint: number | null,
): boolean[] {
  if (current.length === 0) return [];
  if (itemsEqual(previous, current) && flags.length === current.length) {
    return flags;
  }
  if (flags.length === 0 || previous.length === 0) {
    return indexStripeFlags(current.length);
  }
  if (current.length === previous.length + 1 && itemsEqual(previous, current.slice(0, -1))) {
    return [...flags, (current.length - 1) % 2 === 1];
  }
  if (current.length === previous.length - 1 && flags.length === previous.length) {
    const removed = removedIndex(previous, current, removedIndexHint);
    if (removed >= 0 && removed < flags.length) {
      return flags.filter((_, i) => i !== removed);
    }
  }
  return indexStripeFlags(current.length);
}

function indexStripeFlags(length: number): boolean[] {
  return Array.from({ length }, (_, i) => i % 2 === 1);
}

function itemsEqual(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((item, i) => item === b[i]);
}

function removedIndex(
  previous: string[],
  current: string[],
  hint: number | null,
): number {
  if (hint != null && hint >= 0 && hint < previous.length) {
    return hint;
  }

  for (let i = 0; i < current.length; i++) {
    if (previous[i] !== current[i]) return i;
  }
  return previous.length - 1;
}
